// Shared Title Hero block — Figma module 7619:32048 / financing vertical Title Hero family.

import { ArrowRight } from "iconoir-react";

import { ArrowPillLink } from "@/components/ui/arrow-pill-link";
import { Button } from "@/components/ui/button";
import { ArrowLink, Link } from "@/components/ui/link";
import { CtaLocationProvider } from "@/components/ui/link";
import { ScrollRevealText } from "@/components/ui/scroll-reveal-text";
import { cnHeroBelowNavPadding } from "@/lib/nav-layout";
import { cn } from "@/lib/utils";

export interface TitleHeroCta {
  label: string;
  href: string;
  external?: boolean;
}

export type TitleHeroHeadlineScale =
  | "early-stage"
  | "intro"
  | "financing"
  | "contact"
  | "compare-intro"
  | "listing"
  | "title-only";

export interface TitleHeroProps {
  eyebrow?: string;
  title: string;
  description?: string;
  cta?: TitleHeroCta;
  primaryCta?: TitleHeroCta;
  secondaryCta?: TitleHeroCta;
  /** Wholesale: break title on `\n` on mobile, join with spaces on desktop. */
  titleMobileBreakDesktopJoin?: boolean;
  headlineScale?: TitleHeroHeadlineScale;
  descriptionMuted?: boolean;
  minHeight?: "none" | "financing" | "compare";
  /** When false, skip nav clearance (page already renders HeaderSpacer). */
  belowNavPadding?: boolean;
  className?: string;
  contentClassName?: string;
  sectionId?: string;
  contentTestId?: string;
  /** Optional analytics / visual-verify marker. */
  visualId?: string;
  /** e.g. `growing-hero` for section-level QA hooks. */
  dataSection?: string;
  /** Override default `title-hero` section test id. */
  sectionTestId?: string;
  borderBottom?: boolean;
  /** Wholesale drops bottom border below lg. */
  borderBottomMobileOnly?: boolean;
  /** Figma 8196:24733 — eyebrow+headline grouped (8px), 20px to description, 32px to CTA. */
  groupFinancingText?: boolean;
  descriptionClassName?: string;
  /** Load-triggered grey-400 → grey-950 title reveal (WEB-421 / WEB-425). Default on. */
  scrollRevealTitle?: boolean;
}

function linesFromNewlines(value: string) {
  return value.includes("\n") ? value.split("\n").filter(Boolean) : [value];
}

function headlineClassName(scale: TitleHeroHeadlineScale) {
  switch (scale) {
    case "early-stage":
      return "text-grey-950 text-ds-heading-h3 md:text-ds-display-md w-full max-w-[871px] overflow-x-clip text-balance whitespace-pre-line";
    case "intro":
      return "text-grey-950 text-ds-heading-h3 md:text-ds-display-sm w-full max-w-[506px] text-pretty md:text-balance";
    case "contact":
      return "text-ds-heading-h3 md:text-ds-display-sm lg:text-ds-display-md text-grey-950 max-w-[327px] text-balance md:max-w-[912px]";
    case "compare-intro":
      return "text-ds-heading-h3 md:text-ds-display-sm lg:text-ds-display-md text-grey-950 max-w-[327px] text-balance md:max-w-[912px]";
    case "listing":
      return "text-grey-950 text-ds-heading-h3 md:text-ds-display-sm lg:text-ds-display-md max-w-[912px] text-balance whitespace-pre-line";
    case "title-only":
      return "text-grey-950 text-ds-heading-h3 md:text-ds-display-sm lg:text-ds-display-md w-full max-w-full text-balance break-words";
    case "financing":
    default:
      return "text-grey-950 text-ds-heading-h3 lg:text-ds-display-md text-balance";
  }
}

function minHeightClass(minHeight: TitleHeroProps["minHeight"]) {
  switch (minHeight) {
    case "financing":
      return "md:min-h-[380px] md:justify-center lg:min-h-[420px]";
    case "compare":
      return "md:min-h-[380px] md:justify-center lg:min-h-[420px]";
    default:
      return "";
  }
}

function TitleBlock({
  headlineScale,
  title,
  titleMobileBreakDesktopJoin,
  scrollRevealTitle,
}: {
  headlineScale: TitleHeroHeadlineScale;
  title: string;
  titleMobileBreakDesktopJoin: boolean;
  scrollRevealTitle?: boolean;
}) {
  const titleLines = linesFromNewlines(title);
  const titleDesktop = title.replace(/\n/g, " ");
  const hasNewlines = title.includes("\n");

  const titleContent = titleMobileBreakDesktopJoin ? (
    <>
      <span className="lg:hidden">
        {titleLines.map((line, index) => (
          <span key={index} className={index > 0 ? "block" : undefined}>
            {line}
          </span>
        ))}
      </span>
      <span className="hidden lg:inline">{titleDesktop}</span>
    </>
  ) : (
    titleLines.map((line, index) => (
      <span key={index} className={index > 0 ? "block" : undefined}>
        {line}
      </span>
    ))
  );

  // ScrollRevealText flattens React children — pass plain text with `\n` so line
  // breaks and desktop/mobile join behave correctly (wholesale, products, etc.).
  if (scrollRevealTitle) {
    const revealMode = headlineScale === "title-only" ? "whole" : "word";

    return (
      <h1
        className={cn(
          headlineClassName(headlineScale),
          headlineScale === "early-stage" && "min-w-0 self-stretch",
          titleMobileBreakDesktopJoin
            ? "whitespace-pre-line lg:whitespace-normal"
            : hasNewlines && "whitespace-pre-line",
          "max-w-full min-w-0 overflow-x-clip"
        )}
      >
        <ScrollRevealText
          variant="light"
          className="w-full max-w-full min-w-0"
          mode={revealMode}
        >
          {title}
        </ScrollRevealText>
      </h1>
    );
  }

  return <h1 className={headlineClassName(headlineScale)}>{titleContent}</h1>;
}

function DescriptionBlock({
  description,
  headlineScale,
  descriptionMuted,
  className,
}: {
  description: string;
  headlineScale: TitleHeroHeadlineScale;
  descriptionMuted: boolean;
  className?: string;
}) {
  const descriptionLines = linesFromNewlines(description);

  return (
    <p
      className={cn(
        "text-ds-body-lg max-w-[640px] text-balance",
        descriptionMuted ? "text-grey-600" : "text-grey-950",
        headlineScale === "early-stage" && "max-w-[561px]",
        headlineScale === "intro" &&
          "text-ds-body-sm md:text-ds-body-lg max-w-[640px] text-balance",
        headlineScale === "compare-intro" && "mt-3 md:mt-4",
        headlineScale === "financing" &&
          !className &&
          descriptionMuted &&
          "lg:text-grey-600 lg:text-ds-body-md whitespace-pre-line",
        headlineScale === "financing" &&
          !className &&
          !descriptionMuted &&
          "whitespace-pre-line",
        className
      )}
    >
      {descriptionLines.map((line, index) => (
        <span key={index} className={index > 0 ? "block" : undefined}>
          {line}
        </span>
      ))}
    </p>
  );
}

function HeroCtas({
  cta,
  primaryCta,
  secondaryCta,
}: {
  cta?: TitleHeroCta;
  primaryCta?: TitleHeroCta;
  secondaryCta?: TitleHeroCta;
}) {
  const showButtonPair = primaryCta || secondaryCta;
  const showPillCta = cta && !showButtonPair;

  if (!showPillCta && !showButtonPair) return null;

  return (
    <>
      {showPillCta && (
        <ArrowPillLink
          href={cta.href}
          external={cta.external}
          className="mt-1 lg:mt-0"
        >
          {cta.label}
        </ArrowPillLink>
      )}
      {showButtonPair && (
        <div className="mt-2 flex flex-col items-center gap-4 md:flex-row md:gap-6">
          {primaryCta && (
            <Button
              size="lg"
              render={
                <Link href={primaryCta.href} external={primaryCta.external} />
              }
            >
              {primaryCta.label}
              <ArrowRight aria-hidden className="size-4" />
            </Button>
          )}
          {secondaryCta && (
            <ArrowLink
              href={secondaryCta.href}
              external={secondaryCta.external}
            >
              {secondaryCta.label}
            </ArrowLink>
          )}
        </div>
      )}
    </>
  );
}

function TitleHeroContent({
  eyebrow,
  title,
  description,
  cta,
  primaryCta,
  secondaryCta,
  titleMobileBreakDesktopJoin = false,
  headlineScale = "financing",
  descriptionMuted = false,
  minHeight = "none",
  belowNavPadding = true,
  className,
  contentClassName,
  sectionId,
  contentTestId,
  visualId,
  dataSection,
  sectionTestId = "title-hero",
  borderBottom = true,
  borderBottomMobileOnly = false,
  groupFinancingText = false,
  descriptionClassName,
  scrollRevealTitle = true,
}: TitleHeroProps) {
  const sectionProps = {
    id: sectionId,
    "data-testid": sectionTestId,
    "data-visual-id": visualId,
    "data-section": dataSection,
  };

  // Contact + HeaderSpacer — Figma 7620:32083 / book-a-call title hero.
  if (headlineScale === "contact" && !belowNavPadding) {
    return (
      <section
        {...sectionProps}
        className={cn(
          "bg-grey-50 border-grey-100 flex flex-col items-center border-b px-6 py-8 text-center md:py-20 lg:px-8",
          className
        )}
      >
        <div
          data-testid={contentTestId}
          className={cn(
            "flex flex-col items-center gap-3 md:gap-4",
            contentClassName
          )}
        >
          {eyebrow && (
            <p className="text-grey-500 text-ds-body-md">{eyebrow}</p>
          )}
          <TitleBlock
            headlineScale={headlineScale}
            title={title}
            titleMobileBreakDesktopJoin={false}
            scrollRevealTitle={scrollRevealTitle}
          />
          {primaryCta || secondaryCta ? (
            <HeroCtas primaryCta={primaryCta} secondaryCta={secondaryCta} />
          ) : null}
        </div>
      </section>
    );
  }

  // Products / customer-story listing — eyebrow + display headline, no CTA (Figma 8049:15467).
  if (headlineScale === "listing") {
    return (
      <section
        {...sectionProps}
        className={cn(
          "bg-grey-50",
          borderBottom && "border-grey-100 border-b",
          belowNavPadding && cnHeroBelowNavPadding(),
          "px-6 pb-8 lg:px-20 lg:pb-20",
          className
        )}
      >
        <div
          data-testid={contentTestId}
          className={cn(
            "mx-auto flex max-w-[912px] flex-col items-center gap-5 text-center",
            contentClassName
          )}
        >
          {eyebrow && (
            <p className="text-grey-500 text-ds-body-md">{eyebrow}</p>
          )}
          <TitleBlock
            headlineScale={headlineScale}
            title={title}
            titleMobileBreakDesktopJoin={false}
            scrollRevealTitle={scrollRevealTitle}
          />
        </div>
      </section>
    );
  }

  // Title-only hero — display headline, no eyebrow/CTA (Figma 8062:49888).
  if (headlineScale === "title-only") {
    return (
      <section
        {...sectionProps}
        className={cn(
          "bg-grey-50 border-grey-100 overflow-x-clip border-b",
          belowNavPadding && cnHeroBelowNavPadding(),
          "px-6 pb-16 md:px-8 md:pb-20 lg:px-20 lg:pb-20",
          className
        )}
      >
        <div
          data-testid={contentTestId}
          className={cn(
            "mx-auto flex w-full max-w-[912px] min-w-0 flex-col items-center text-center",
            contentClassName
          )}
        >
          <TitleBlock
            headlineScale={headlineScale}
            title={title}
            titleMobileBreakDesktopJoin={false}
            scrollRevealTitle={scrollRevealTitle}
          />
        </div>
      </section>
    );
  }

  // Compare-youlend intro — Figma 9644:29757; flat section, no inner wrapper.
  if (headlineScale === "compare-intro") {
    return (
      <section
        {...sectionProps}
        className={cn(
          "bg-grey-50 border-grey-100 flex flex-col items-center border-b px-6 py-8 text-center md:min-h-[380px] md:justify-center md:px-8 md:py-20 lg:min-h-[420px] lg:px-20",
          belowNavPadding && cnHeroBelowNavPadding(),
          className
        )}
      >
        <TitleBlock
          headlineScale={headlineScale}
          title={title}
          titleMobileBreakDesktopJoin={false}
          scrollRevealTitle={scrollRevealTitle}
        />
        {description && (
          <DescriptionBlock
            description={description}
            headlineScale={headlineScale}
            descriptionMuted={false}
          />
        )}
      </section>
    );
  }

  // Early-stage / growing — Figma 8241:21531 / 8301:72950.
  // Intro — Figma Title Hero 8317-59674 (desktop) / 8317-59684 (mobile).
  if (headlineScale === "early-stage" || headlineScale === "intro") {
    return (
      <section
        {...sectionProps}
        className={cn(
          "bg-grey-50",
          borderBottom && "border-grey-100 border-b",
          belowNavPadding && cnHeroBelowNavPadding(),
          "overflow-x-clip px-6 pb-8 md:px-8 md:pb-20 lg:px-20 lg:pb-20",
          className
        )}
      >
        <div
          data-testid={contentTestId}
          className={cn(
            "mx-auto flex w-full max-w-[904px] min-w-0 flex-col items-center gap-3 text-center md:gap-8",
            contentClassName
          )}
        >
          <div className="flex w-full flex-col items-center gap-3 md:gap-5">
            <div className="flex w-full min-w-0 flex-col items-center gap-3 md:gap-2">
              {eyebrow && (
                <p className="text-grey-500 text-ds-body-md">{eyebrow}</p>
              )}
              <TitleBlock
                headlineScale={headlineScale}
                title={title}
                titleMobileBreakDesktopJoin={false}
                scrollRevealTitle={scrollRevealTitle}
              />
            </div>
            {description && (
              <DescriptionBlock
                description={description}
                headlineScale={headlineScale}
                descriptionMuted={descriptionMuted}
              />
            )}
          </div>
          <HeroCtas cta={cta} />
        </div>
      </section>
    );
  }

  // Wholesale financing — Figma 8218:20165 / 9640:29997.
  if (titleMobileBreakDesktopJoin) {
    return (
      <section
        {...sectionProps}
        className={cn(
          "bg-grey-50 border-grey-100 border-b max-lg:border-b-0",
          belowNavPadding && cnHeroBelowNavPadding(),
          "px-6 pb-8 lg:px-20 lg:pb-20",
          className
        )}
      >
        <div
          data-testid={contentTestId}
          className={cn(
            "mx-auto flex max-w-[904px] flex-col items-center gap-3 py-8 text-center lg:gap-5 lg:py-0",
            contentClassName
          )}
        >
          <TitleBlock
            headlineScale="financing"
            title={title}
            titleMobileBreakDesktopJoin
            scrollRevealTitle={scrollRevealTitle}
          />
          {description && (
            <DescriptionBlock
              description={description}
              headlineScale="financing"
              descriptionMuted={false}
            />
          )}
          <HeroCtas cta={cta} />
        </div>
      </section>
    );
  }

  // Default financing vertical (DTC, established, etc.) — Figma 8049:15377 / 7619:32048.
  return (
    <section
      {...sectionProps}
      className={cn(
        "bg-grey-50 flex flex-col items-center text-center",
        borderBottom && "border-grey-100 border-b",
        borderBottomMobileOnly && "max-lg:border-b-0",
        belowNavPadding && cnHeroBelowNavPadding(),
        "px-6 pb-8 md:px-8 md:pb-12 lg:px-20 lg:pb-20",
        minHeightClass(minHeight),
        className
      )}
    >
      <div
        data-testid={contentTestId}
        className={cn(
          "mx-auto flex w-full max-w-[912px] flex-col items-center",
          groupFinancingText
            ? "gap-8"
            : "gap-3 py-8 md:gap-5 md:py-12 lg:gap-8 lg:py-20",
          contentClassName
        )}
      >
        {groupFinancingText ? (
          <>
            <div className="flex flex-col items-center gap-5">
              <div className="flex flex-col items-center gap-2">
                {eyebrow && (
                  <p className="text-grey-500 text-ds-body-md">{eyebrow}</p>
                )}
                <TitleBlock
                  headlineScale="financing"
                  title={title}
                  titleMobileBreakDesktopJoin={false}
                  scrollRevealTitle={scrollRevealTitle}
                />
              </div>
              {description && (
                <DescriptionBlock
                  description={description}
                  headlineScale="financing"
                  descriptionMuted={descriptionMuted}
                  className={descriptionClassName}
                />
              )}
            </div>
            <HeroCtas
              cta={cta}
              primaryCta={primaryCta}
              secondaryCta={secondaryCta}
            />
          </>
        ) : (
          <>
            {eyebrow && (
              <p className="text-grey-500 text-ds-body-md">{eyebrow}</p>
            )}
            <TitleBlock
              headlineScale="financing"
              title={title}
              titleMobileBreakDesktopJoin={false}
              scrollRevealTitle={scrollRevealTitle}
            />
            {description && (
              <DescriptionBlock
                description={description}
                headlineScale="financing"
                descriptionMuted={descriptionMuted}
                className={descriptionClassName}
              />
            )}
            <HeroCtas
              cta={cta}
              primaryCta={primaryCta}
              secondaryCta={secondaryCta}
            />
          </>
        )}
      </div>
    </section>
  );
}

export function TitleHero(props: TitleHeroProps) {
  return (
    <CtaLocationProvider location="hero">
      <TitleHeroContent {...props} />
    </CtaLocationProvider>
  );
}
